import { useState } from 'react';
import { useStaffReconciliation } from '../utils/useStaffReconciliation';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { useAuth } from '../contexts/GoogleAuthContext';
import { myGuidesAdminApi, OrchestratorPerson, StaffTier } from '../services/my-guides-admin-api';
import NotificationSnackbar from '../components/NotificationSnackbar';
import PersonSearchBox from '../components/PersonSearchBox';
import { nameFromEmail } from '../utils/featureFlags';
import { personInitial, personLabel } from '../utils/people';
import { canChangeTierOf, canGrantTier, needsChange, staffRowState } from '../utils/staffTiers';

const TIER_LABELS: Record<StaffTier, string> = {
  none: 'Not staff',
  staff: 'Staff',
  admin: 'Admin',
};

const ADMIN_ONLY_HINT = "Only Command Center admins can change an admin's tier";

interface PendingChange {
  user: OrchestratorPerson;
  tier: StaffTier;
}

function formatAdded(user: OrchestratorPerson): string {
  if (!user.updatedAt) return '—';
  const date = new Date(user.updatedAt);
  if (Number.isNaN(date.getTime())) return '—';
  const who = nameFromEmail(user.updatedBy);
  return `${format(date, 'MMM d')}${who ? ` · ${who}` : ''}`;
}

export default function Staff() {
  const { user: currentUser, hasPermission } = useAuth();
  const canEdit =
    (currentUser?.role === 'developer' || currentUser?.role === 'admin') &&
    hasPermission('staff', 'edit');
  // Only Command Center admins grant the admin tier or change an admin's tier; developers
  // move people between none and staff.
  const isCommandCenterAdmin = currentUser?.role === 'admin';
  const canGrantAdmin = canGrantTier('admin', isCommandCenterAdmin);
  const queryClient = useQueryClient();
  const reconciliation = useStaffReconciliation(currentUser?.email ?? '', myGuidesAdminApi.getStaff);

  const [pending, setPending] = useState<PendingChange | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const { data: staff, isLoading, error } = useQuery('staff', myGuidesAdminApi.getStaff, { retry: false });

  const mutation = useMutation(
    ({ user, tier }: PendingChange) => myGuidesAdminApi.setStaffTier(user.personId, tier),
    {
      retry: false,
      onSuccess: (_result, { user, tier }) => {
        queryClient.invalidateQueries('staff');
        queryClient.invalidateQueries(['orchestratorPersonSearch']);
        setPending(null);
        setSnackbar({
          open: true,
          severity: 'success',
          message:
            tier === 'none'
              ? `${personLabel(user)} is no longer staff`
              : `${personLabel(user)} is now ${TIER_LABELS[tier].toLowerCase()}`,
        });
      },
      onError: (e: Error & { details?: { outcome?: string } }, { user }) => {
        if (e.details?.outcome === 'unknown') reconciliation.hold(user.personId);
        setPending(null);
        setSnackbar({ open: true, severity: 'error', message: `Could not change staff: ${e.message}` });
      },
    }
  );

  // Changes that widen or remove access ask first; moving between staff and admin does too.
  const requestChange = (user: OrchestratorPerson, tier: StaffTier) => {
    if (
      reconciliation.isBlocked(user.personId) ||
      !needsChange(user, tier) ||
      !canGrantTier(tier, canGrantAdmin) ||
      !canChangeTierOf(user, isCommandCenterAdmin)
    )
      return;
    setPending({ user, tier });
  };

  const members = staff ?? [];
  const staffIds = members.map((m) => m.personId);

  return (
    <Box>
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Tripsby staff
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
          Staff get every flag that is on for "Tripsby staff". They also see the Team badge and the
          team-only switches. Admins are staff with admin powers, and only Command Center admins can
          make someone an admin or change an admin's tier.
        </Typography>
      </Box>

      <Alert severity="info" icon={false} sx={{ mb: 2.5, maxWidth: 720 }}>
        Only people with a <strong>verified @tripsby.ai or @byai.group email</strong> can be staff.
        Anyone else is refused, and a staff role on any other email is ignored when they sign in.
      </Alert>

      {canEdit && (
        <Box sx={{ maxWidth: 720, mb: 3 }}>
          <PersonSearchBox
            placeholder="Search by email, for example michael@"
            exclude={staffIds}
            renderNote={(user) =>
              user.eligibleForStaff ? null : (
                <Box component="span" sx={{ color: 'error.main', ml: 1 }}>
                  Not a Tripsby email
                </Box>
              )
            }
            renderAction={(user, clear) =>
              user.eligibleForStaff ? (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Button
                    size="small"
                    disabled={reconciliation.isBlocked(user.personId)}
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    onClick={() => {
                      requestChange(user, 'staff');
                      clear();
                    }}
                  >
                    Make staff
                  </Button>
                  {canGrantAdmin && (
                    <Button
                      size="small"
                      disabled={reconciliation.isBlocked(user.personId)}
                      variant="outlined"
                      onClick={() => {
                        requestChange(user, 'admin');
                        clear();
                      }}
                    >
                      Admin
                    </Button>
                  )}
                </Box>
              ) : (
                <Button size="small" disabled>
                  Can't be staff
                </Button>
              )
            }
          />
        </Box>
      )}

      {reconciliation.blocked.map((id) => (
        <Alert key={id} severity="warning" sx={{ mb: 2 }}
          action={<Button onClick={() => void reconciliation.verify(id)}>Verify current tier</Button>}>
          A staff change may have applied. Changes for {id} are blocked until a fresh current tier is verified.
        </Alert>
      ))}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message}
        </Alert>
      ) : null}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead sx={{ bgcolor: '#F7F7FA' }}>
              <TableRow>
                <TableCell>Person</TableCell>
                <TableCell>Tripsby account</TableCell>
                <TableCell>Tier</TableCell>
                <TableCell>Added</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((member) => (
                <TableRow key={member.personId}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: 'primary.light' }}>
                        {personInitial(member)}
                      </Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {personLabel(member)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{member.email}</TableCell>
                  <TableCell>
                    {staffRowState(member) === 'ignored' ? (
                      // A staff role on a non-Tripsby email: Orchestrator ignores it, so it
                      // is not shown as access. Only Remove is offered.
                      <Chip
                        size="small"
                        label="Ignored: not a Tripsby email"
                        sx={{ bgcolor: 'rgba(0,0,0,0.06)', color: 'text.secondary', fontWeight: 600 }}
                      />
                    ) : canEdit ? (
                      <Select
                        size="small"
                        disabled={reconciliation.isBlocked(member.personId) || !canChangeTierOf(member, isCommandCenterAdmin)}
                        title={canChangeTierOf(member, isCommandCenterAdmin) ? undefined : ADMIN_ONLY_HINT}
                        value={staffRowState(member)}
                        onChange={(e) => requestChange(member, e.target.value as StaffTier)}
                        sx={{ minWidth: 110 }}
                        inputProps={{ 'aria-label': `Tier for ${personLabel(member)}` }}
                      >
                        <MenuItem value="staff">Staff</MenuItem>
                        <MenuItem value="admin" disabled={!canGrantAdmin}>
                          Admin
                        </MenuItem>
                      </Select>
                    ) : (
                      <Chip
                        size="small"
                        label={TIER_LABELS[staffRowState(member) === 'admin' ? 'admin' : 'staff']}
                        sx={{ bgcolor: 'rgba(84,143,101,0.08)', color: '#3C5945', fontWeight: 600 }}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{formatAdded(member)}</TableCell>
                  <TableCell align="right">
                    {canEdit ? (
                      <Box
                        component="span"
                        title={canChangeTierOf(member, isCommandCenterAdmin) ? undefined : ADMIN_ONLY_HINT}
                      >
                        <Button
                          size="small"
                          color="error"
                          disabled={reconciliation.isBlocked(member.personId) || !canChangeTierOf(member, isCommandCenterAdmin)}
                          onClick={() => requestChange(member, 'none')}
                          sx={{ fontWeight: 600 }}
                        >
                          Remove
                        </Button>
                      </Box>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {members.length === 0 && !error && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
                    No staff yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, maxWidth: 720 }}>
        Someone must have signed in to Tripsby once before you can make them staff. Removing staff
        takes effect on their next sign-in, or within a minute.
      </Typography>

      <Dialog open={Boolean(pending)} onClose={() => !mutation.isLoading && setPending(null)}>
        <DialogTitle>
          {pending?.tier === 'none'
            ? 'Remove from staff?'
            : pending?.tier === 'admin'
            ? 'Make admin?'
            : 'Make staff?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pending?.tier === 'none' &&
              (staffRowState(pending.user) === 'ignored'
                ? `${personLabel(pending.user)} holds a staff role on an email that is not a Tripsby address, so it is already ignored. Removing clears the role. It is logged in the audit log.`
                : `${personLabel(pending.user)} will lose the staff tier and every flag that is on for staff. It is logged in the audit log.`)}
            {pending?.tier === 'staff' &&
              `${pending ? personLabel(pending.user) : ''} will get every flag that is on for Tripsby staff, plus the Team badge and team-only switches.`}
            {pending?.tier === 'admin' &&
              `${pending ? personLabel(pending.user) : ''} will be staff with admin powers, and will get every flag that is on for Tripsby staff or Tripsby admins.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)} disabled={mutation.isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={pending?.tier === 'none' ? 'error' : 'primary'}
            disabled={mutation.isLoading || Boolean(pending && reconciliation.isBlocked(pending.user.personId))}
            onClick={() => pending && !reconciliation.isBlocked(pending.user.personId) && mutation.mutate(pending)}
          >
            {mutation.isLoading
              ? 'Saving...'
              : pending?.tier === 'none'
              ? 'Remove'
              : pending?.tier === 'admin'
              ? 'Make admin'
              : 'Make staff'}
          </Button>
        </DialogActions>
      </Dialog>

      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      />
    </Box>
  );
}
